import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type Divisao, type Representante, type Usuario } from '../api'
import { conferirMetas, montarEnvioMetas, type LinhaMeta } from '../conferenciaMetas'
import { ComemoracaoMarca } from '../componentes/ComemoracaoMarca'
import { duracaoDaFesta, type TipoDeFesta } from '../marcosMarca'
import { AbaMarcas } from '../componentes/admin/AbaMarcas'
import { AbaMetas } from '../componentes/admin/AbaMetas'
import { AbaFontes } from '../componentes/admin/AbaFontes'
import {
  montarEnvioDivisoes,
  rascunhoDe,
  rascunhoNovo,
  textoDeMeta,
  TETO_LOGO_BYTES,
  type RascunhoDivisao,
  type RespostaCadastro,
  type RespostaFontes,
} from '../componentes/admin/cadastroAdmin'

/**
 * A tela de administração: marcas, metas e fontes.
 *
 * É por ela que se cadastra meta e marca, e é por ela que se descobre, no meio
 * do evento, que o número parou. O estado das três abas vive AQUI, num rascunho
 * só, porque metas e marcas são a mesma lista de divisões vista de dois
 * ângulos — trocar de aba não pode perder o que estava digitado na outra.
 *
 * O papel não é segurança: o servidor barra quem não é `admin` em toda rota
 * `/api/admin/*`. Esta tela só não oferece caminho que vai dar 403.
 */

type Aba = 'marcas' | 'metas' | 'fontes'

const ABAS: Array<{ id: Aba; rotulo: string }> = [
  { id: 'marcas', rotulo: 'Marcas' },
  { id: 'metas', rotulo: 'Metas' },
  { id: 'fontes', rotulo: 'Fontes' },
]

/** De quanto em quanto tempo a aba Fontes se atualiza sozinha, em ms. */
const INTERVALO_FONTES = 15_000

/** A festa que a aba Marcas está mostrando — valor e ranking do instante do clique. */
type EnsaioDeFesta = {
  divisao: Divisao
  tipo: TipoDeFesta
  representantes: Representante[]
}

/** O aviso "salvo." que aparece e desaparece — `.salvo.ver` do protótipo. */
function usarAvisoSalvo(): [boolean, () => void] {
  const [ver, setVer] = useState(false)
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (relogio.current) clearTimeout(relogio.current) }, [])
  const avisar = useCallback(() => {
    setVer(true)
    if (relogio.current) clearTimeout(relogio.current)
    relogio.current = setTimeout(() => setVer(false), 2200)
  }, [])
  return [ver, avisar]
}

/** Mensagem do servidor, sem interpretar: quem lê é quem cadastra. */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : String(erro)
}

type Props = {
  usuario: Usuario
  aoSair: () => void
  aoVoltar: () => void
}

export function Admin({ usuario, aoSair, aoVoltar }: Props) {
  const [aba, setAba] = useState<Aba>('marcas')
  const [cadastro, setCadastro] = useState<RespostaCadastro | null>(null)
  const [rascunhos, setRascunhos] = useState<RascunhoDivisao[]>([])
  const [metaGeral, setMetaGeral] = useState('')
  const [erroCarga, setErroCarga] = useState<string | null>(null)

  const [salvandoMarcas, setSalvandoMarcas] = useState(false)
  const [erroMarcas, setErroMarcas] = useState<string | null>(null)
  const [salvoMarcas, avisarMarcas] = usarAvisoSalvo()

  const [salvandoMetas, setSalvandoMetas] = useState(false)
  const [erroMetas, setErroMetas] = useState<string | null>(null)
  const [avisosMetas, setAvisosMetas] = useState<string[]>([])
  const [salvoMetas, avisarMetas] = usarAvisoSalvo()

  const [fontes, setFontes] = useState<RespostaFontes | null>(null)
  const [recarregando, setRecarregando] = useState<string | null>(null)
  const [avisoFonte, setAvisoFonte] = useState<string | null>(null)
  const [erroFonte, setErroFonte] = useState<string | null>(null)
  const [alternandoFiltro, setAlternandoFiltro] = useState(false)
  const [erroFiltro, setErroFiltro] = useState<string | null>(null)

  /* Contador de linhas novas: só precisa ser único dentro desta sessão de
     edição, e por isso não vai para o estado (mudá-lo não repinta nada). */
  const novasCriadas = useRef(0)

  // ----------------------------------------------------------------- carga

  const carregar = useCallback(async () => {
    try {
      /* `api.admin.cadastro` tipa só o que o painel usa; a rota devolve mais
         (base, `ativa`, de-para do ERP, resumo). O tipo local em
         `cadastroAdmin.ts` é o espelho do servidor — alargar o cliente
         compartilhado na véspera mexeria no dashboard também. */
      const resposta = (await api.admin.cadastro()) as unknown as RespostaCadastro
      setCadastro(resposta)
      setRascunhos(resposta.divisoes.map(rascunhoDe))
      setMetaGeral(textoDeMeta(resposta.metaGeral))
      setErroCarga(null)
    } catch (erro) {
      setErroCarga(mensagemDe(erro))
    }
  }, [])

  useEffect(() => { void carregar() }, [carregar])

  const carregarFontes = useCallback(async () => {
    try {
      const resposta = (await api.admin.fontes()) as unknown as RespostaFontes
      setFontes(resposta)
      setErroFonte(null)
    } catch (erro) {
      /* Falha de consulta não apaga o quadro anterior: o estado velho com a
         idade à vista informa mais do que uma tela em branco. */
      setErroFonte(mensagemDe(erro))
    }
  }, [])

  /* A aba Fontes se atualiza sozinha enquanto está aberta: "a fonte caiu" não
     tem evento — o que muda é a idade da última coleta. Fora da aba o laço não
     roda, para não consultar o servidor por 4 dias à toa. */
  useEffect(() => {
    if (aba !== 'fontes') return
    void carregarFontes()
    const laco = setInterval(() => { void carregarFontes() }, INTERVALO_FONTES)
    return () => clearInterval(laco)
  }, [aba, carregarFontes])

  // -------------------------------------------------------------- rascunho

  const alterar = useCallback((chave: string, mudanca: Partial<RascunhoDivisao>) => {
    setRascunhos(atuais => atuais.map(r => (r.chave === chave ? { ...r, ...mudanca } : r)))
  }, [])

  const ordemDosSubitens = useMemo(
    () => (cadastro?.subitensDisponiveis ?? []).map(subitem => subitem.codigo),
    [cadastro],
  )

  const alternarSubitem = useCallback((chave: string, codigo: string) => {
    setRascunhos(atuais => atuais.map(r => {
      if (r.chave !== chave) return r
      const tem = r.subitens.includes(codigo)
      /* Reordena pela ordem do catálogo, não pela ordem dos cliques: a posição
         na lista é a `ordem` que o servidor grava, e é ela que o card exibe. */
      const subitens = tem
        ? r.subitens.filter(item => item !== codigo)
        : ordemDosSubitens.filter(item => item === codigo || r.subitens.includes(item))
      return { ...r, subitens }
    }))
  }, [ordemDosSubitens])

  const escolherLogo = useCallback((chave: string, arquivo: File | null) => {
    if (!arquivo) return
    setErroMarcas(null)
    if (arquivo.size > TETO_LOGO_BYTES) {
      setErroMarcas(
        `"${arquivo.name}" tem ${Math.round(arquivo.size / 1024)} KB e o teto é 512 KB.`,
      )
      return
    }
    /* Prévia local antes de qualquer ida ao servidor; o arquivo em si só sobe
       no "Salvar marcas", porque `POST /api/admin/logo/:id` responde 404
       enquanto a marca não existe. */
    const leitor = new FileReader()
    leitor.onload = () => {
      const previa = typeof leitor.result === 'string' ? leitor.result : null
      alterar(chave, { arquivo, previa })
    }
    leitor.onerror = () => setErroMarcas(`Não foi possível ler "${arquivo.name}".`)
    leitor.readAsDataURL(arquivo)
  }, [alterar])

  const adicionarMarca = useCallback(() => {
    setRascunhos(atuais => {
      const maiorOrdem = atuais.reduce((maior, r) => Math.max(maior, Number(r.ordem) || 0), 0)
      novasCriadas.current += 1
      return [...atuais, rascunhoNovo(maiorOrdem + 1, `nova:${novasCriadas.current}`)]
    })
  }, [])

  const descartar = useCallback((chave: string) => {
    setRascunhos(atuais => atuais.filter(r => r.chave !== chave))
  }, [])

  // --------------------------------------------------------------- gravação

  const salvarMarcas = useCallback(async () => {
    const envio = montarEnvioDivisoes(rascunhos, cadastro?.bases ?? [])
    if (!envio.ok) {
      setErroMarcas(envio.problemas.join(' '))
      return
    }
    setSalvandoMarcas(true)
    setErroMarcas(null)
    try {
      await api.admin.salvarDivisoes(envio.divisoes)

      /* As logos vão DEPOIS, e uma por uma: cada uma é um POST próprio e o
         divisor que ele exige acabou de ser criado pelo PUT acima. */
      const enviadas: Record<string, string> = {}
      for (const rascunho of rascunhos) {
        if (!rascunho.arquivo) continue
        const resposta = await api.admin.enviarLogo(rascunho.id.trim().toLowerCase(), rascunho.arquivo)
        enviadas[rascunho.chave] = resposta.logo
      }

      /* Marca gravada deixa de ser nova e ganha a chave definitiva. Recarregar
         o cadastro daqui seria mais simples e jogaria fora as metas que estão
         sendo digitadas na outra aba. */
      setRascunhos(atuais => atuais.map(r => ({
        ...r,
        chave: r.nova ? `divisao:${r.id.trim().toLowerCase()}` : r.chave,
        id: r.id.trim().toLowerCase(),
        nova: false,
        arquivo: null,
        logo: enviadas[r.chave] ?? r.logo,
        /* O que acabou de subir é o novo "gravado": sem isso o próximo salvar
           reenviaria as listas e apagaria a `obs` do de-para. */
        marcasErpGravadas: r.marcasErp,
        catalogosErpGravados: r.catalogosErp,
        marcasLinxGravadas: r.marcasLinx,
        subitensGravados: [...r.subitens],
      })))
      avisarMarcas()
    } catch (erro) {
      setErroMarcas(mensagemDe(erro))
    } finally {
      setSalvandoMarcas(false)
    }
  }, [rascunhos, cadastro, avisarMarcas])

  const rotulosDeSubitem = useMemo(() => {
    const mapa: Record<string, string> = {}
    for (const subitem of cadastro?.subitensDisponiveis ?? []) mapa[subitem.codigo] = subitem.rotulo
    return mapa
  }, [cadastro])

  const linhasMeta = useMemo<LinhaMeta[]>(() => rascunhos.map(rascunho => ({
    chave: rascunho.chave,
    id: rascunho.id.trim().toLowerCase(),
    nome: rascunho.nome,
    ativa: rascunho.ativa,
    nova: rascunho.nova,
    meta: rascunho.meta,
    subitens: rascunho.subitens.map(codigo => ({
      codigo,
      rotulo: rotulosDeSubitem[codigo] ?? codigo,
      meta: rascunho.metasSubitem[codigo] ?? '',
      /* O rascunho guarda o que o servidor tem; o subitem só marcado agora
         ainda não existe lá, e mandar meta para ele derruba o PUT inteiro. */
      novo: rascunho.nova || !rascunho.subitensGravados.includes(codigo),
    })),
  })), [rascunhos, rotulosDeSubitem])

  const conferencia = useMemo(
    () => conferirMetas(metaGeral, linhasMeta),
    [metaGeral, linhasMeta],
  )

  const alterarMeta = useCallback((chave: string, texto: string) => {
    alterar(chave, { meta: texto })
  }, [alterar])

  const alterarMetaSubitem = useCallback((chave: string, codigo: string, texto: string) => {
    setRascunhos(atuais => atuais.map(r => (
      r.chave === chave
        ? { ...r, metasSubitem: { ...r.metasSubitem, [codigo]: texto } }
        : r
    )))
  }, [])

  const salvarMetas = useCallback(async () => {
    const envio = montarEnvioMetas(metaGeral, linhasMeta)
    if (!envio.ok) {
      setErroMetas(envio.problemas.join(' '))
      /* Aviso da tentativa anterior ao lado do erro da atual faz quem cadastra
         ler as duas como se fossem da mesma ação. */
      setAvisosMetas([])
      return
    }
    setSalvandoMetas(true)
    setErroMetas(null)
    setAvisosMetas(envio.avisos)
    try {
      await api.admin.salvarMetas(envio.metaGeral, envio.metas)
      avisarMetas()
    } catch (erro) {
      setErroMetas(mensagemDe(erro))
    } finally {
      setSalvandoMetas(false)
    }
  }, [metaGeral, linhasMeta, avisarMetas])

  const recarregarFonte = useCallback(async (fonte: string | null) => {
    setRecarregando(fonte ?? 'todas')
    setErroFonte(null)
    setAvisoFonte(null)
    try {
      const resposta = await api.admin.recarregar(fonte)
      /* `atendida: false` é a verdade quando não há coletor escutando — dizer
         "recarregado" sem nada acontecer é pior que não oferecer o botão. */
      setAvisoFonte(resposta.atendida
        ? 'Recarga pedida ao coletor.'
        : 'Pedido registrado, mas nenhum coletor está escutando agora.')
      await carregarFontes()
    } catch (erro) {
      setErroFonte(mensagemDe(erro))
    } finally {
      setRecarregando(null)
    }
  }, [carregarFontes])

  const alternarFiltroFaturamento = useCallback(async (ligado: boolean, de?: string, ate?: string) => {
    setAlternandoFiltro(true)
    setErroFiltro(null)
    try {
      const { filtroFaturamento } = await api.admin.definirFiltroFaturamento(ligado, de, ate)
      /* Grava o que o SERVIDOR devolveu, não o que foi pedido: a aba se
         atualiza sozinha a cada 10 s, e sem isto o interruptor voltaria ao
         estado antigo por um instante — piscando a resposta errada na tela de
         quem acabou de clicar. */
      setFontes(atuais => (atuais === null ? atuais : { ...atuais, filtroFaturamento }))
    } catch (erro) {
      setErroFiltro(mensagemDe(erro))
      // Recarrega para a tela mostrar o estado REAL depois de uma falha.
      await carregarFontes()
    } finally {
      setAlternandoFiltro(false)
    }
  }, [carregarFontes])

  /* ---------------------------------------------------------------------
     ENSAIO DA COMEMORAÇÃO (16/10).

     Os botões da aba Marcas mostram a festa daquela marca NESTA tela, com o
     valor e o ranking de agora. Três decisões:

      - o número vem do PAINEL no instante do clique, e não do rascunho: o que a
        festa exibe é o VENDIDO, e o rascunho do admin só tem cadastro;
      - **não gasta a festa real.** Nada é gravado como celebrado e a fila do
        telão não é tocada — quando a marca bater a meta de verdade, a festa
        dela acontece igual. É a mesma regra do ensaio por endereço
        (`?festa=lez:meta`), que existe porque a festa real acontece uma vez
        por marca no evento e ninguém pode ensaiá-la;
      - **fica só nesta aba.** O telão da sala não vê nada: não há rota nem
        evento envolvido, é a mesma tela de festa renderizada aqui.

     Dura o mesmo que a real e sai sozinha; clique ou `Esc` encerram antes.
     --------------------------------------------------------------------- */
  const [ensaio, setEnsaio] = useState<EnsaioDeFesta | null>(null)
  const [ensaiando, setEnsaiando] = useState<string | null>(null)
  const [erroEnsaio, setErroEnsaio] = useState<string | null>(null)
  const relogioEnsaio = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fecharEnsaio = useCallback(() => {
    if (relogioEnsaio.current !== null) {
      clearTimeout(relogioEnsaio.current)
      relogioEnsaio.current = null
    }
    setEnsaio(null)
  }, [])

  const ensaiarFesta = useCallback(async (id: string, tipo: TipoDeFesta) => {
    setErroEnsaio(null)
    setEnsaiando(id)
    try {
      /* As duas consultas em paralelo: uma traz o número da marca, a outra o
         ranking que a festa mostra, e nenhuma depende da outra. */
      const [painel, visao] = await Promise.all([api.painel(), api.marca(id)])
      const divisao = painel.divisoes.find(item => item.id === id)
      if (divisao === undefined) {
        setErroEnsaio(`A marca "${id}" não está no painel. Salve o cadastro e tente de novo.`)
        return
      }
      if (relogioEnsaio.current !== null) clearTimeout(relogioEnsaio.current)
      setEnsaio({ divisao, tipo, representantes: visao.representantes ?? [] })
      relogioEnsaio.current = setTimeout(() => {
        relogioEnsaio.current = null
        setEnsaio(null)
      }, duracaoDaFesta(tipo))
    } catch (erro) {
      setErroEnsaio(mensagemDe(erro))
    } finally {
      setEnsaiando(null)
    }
  }, [])

  /* `Esc` fecha, como em qualquer tela cheia — e o ouvinte só existe enquanto
     há festa no ar, para o admin não carregar um `keydown` global à toa. */
  useEffect(() => {
    if (ensaio === null) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.code === 'Escape') fecharEnsaio()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [ensaio, fecharEnsaio])

  useEffect(
    () => () => {
      if (relogioEnsaio.current !== null) clearTimeout(relogioEnsaio.current)
    },
    [],
  )

  // ------------------------------------------------------------------ tela

  return (
    <div className="admin">
      <header className="admin-cabecalho">
        <div>
          <div className="admin-titulo">War Room · Administração</div>
          <div className="admin-sub">Metas e marcas do painel comercial</div>
        </div>
        <div className="admin-usuario">
          <b>{usuario.nome}</b>
          {usuario.papel}
          <div className="acoes-cabecalho">
            <button className="botao" onClick={aoVoltar}>Painel</button>
            <button className="botao" onClick={aoSair}>Sair</button>
          </div>
        </div>
      </header>

      <nav className="abas" role="tablist">
        {ABAS.map(item => (
          <button
            key={item.id} role="tab" aria-selected={aba === item.id}
            onClick={() => setAba(item.id)}
          >
            {item.rotulo}
          </button>
        ))}
      </nav>

      <main>
        {erroCarga ? <p className="erro">{erroCarga}</p> : null}

        {aba === 'marcas' ? (
          <AbaMarcas
            rascunhos={rascunhos}
            bases={cadastro?.bases ?? []}
            subitensDisponiveis={cadastro?.subitensDisponiveis ?? []}
            aoAlterar={alterar}
            aoAlternarSubitem={alternarSubitem}
            aoEscolherLogo={escolherLogo}
            aoAdicionar={adicionarMarca}
            aoDescartar={descartar}
            aoSalvar={() => { void salvarMarcas() }}
            salvando={salvandoMarcas}
            salvo={salvoMarcas}
            erro={erroMarcas}
            aoEnsaiarFesta={(id, tipo) => { void ensaiarFesta(id, tipo) }}
            ensaiando={ensaiando}
            erroEnsaio={erroEnsaio}
          />
        ) : aba === 'metas' ? (
          <AbaMetas
            ano={cadastro?.ano ?? new Date().getFullYear()}
            metaGeral={metaGeral}
            linhas={linhasMeta}
            conferencia={conferencia}
            aoAlterarMetaGeral={setMetaGeral}
            aoAlterarMeta={alterarMeta}
            aoAlterarMetaSubitem={alterarMetaSubitem}
            aoSalvar={() => { void salvarMetas() }}
            salvando={salvandoMetas}
            salvo={salvoMetas}
            erro={erroMetas}
            avisos={avisosMetas}
          />
        ) : (
          <AbaFontes
            dados={fontes}
            aoRecarregar={fonte => { void recarregarFonte(fonte) }}
            recarregando={recarregando}
            aviso={avisoFonte}
            erro={erroFonte}
            aoAlternarFiltroFaturamento={(ligado, de, ate) => { void alternarFiltroFaturamento(ligado, de, ate) }}
            alternandoFiltro={alternandoFiltro}
            erroFiltro={erroFiltro}
          />
        )}
      </main>

      {/* A festa ensaiada, por cima de tudo. `role="presentation"` na camada:
          quem anuncia é a própria `ComemoracaoMarca`, que já é `role="status"`,
          e dois anúncios para a mesma coisa é o leitor de tela lendo em
          duplicata. */}
      {ensaio === null ? null : (
        <div className="ensaio-festa" onClick={fecharEnsaio} role="presentation">
          <ComemoracaoMarca
            projetor={false}
            divisao={ensaio.divisao}
            tipo={ensaio.tipo}
            representantes={ensaio.representantes}
          />
          {/* Dizer que é ENSAIO é obrigatório: a tela é idêntica à real, e quem
              passar atrás da cadeira vai achar que a marca bateu a meta. */}
          <div className="ensaio-festa-dica">
            ensaio · só nesta tela, o telão não mostrou nada — clique ou Esc para fechar
          </div>
        </div>
      )}
    </div>
  )
}
